import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { VoucherForCampaignListByHotelIdDocument } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { VoucherForCampaignCreate_EditPage_Mutation } from "./mutation";
import { viewModelToInputConverter } from "../../existing/_shared/mutation/composition";
import { VoucherForCampaignNewItem_EditPage_Query } from "../query/query";

import type { MarketingCampaignFormModel } from "../../../type";

type Props = {
	onResetViewModel: () => void;
};

export const useCampaignEditMutation = ({ onResetViewModel }: Props) => {
	const navigate = useNavigate();
	const onGenerateCampaignTopPath = useCallback(
		(hotelID: string) => generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID }),
		[],
	);
	const onTransitionToCampaignTop = useCallback(
		(hotelID: string) => {
			navigate(onGenerateCampaignTopPath(hotelID));
		},
		[onGenerateCampaignTopPath, navigate],
	);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { mutationFunc } = useCustomMutation(VoucherForCampaignCreate_EditPage_Mutation);

	const onSave = useCallback(
		(
			viewModel: MarketingCampaignFormModel["ViewModel"], //
		) => {
			showLoadingScreenCallback();

			const input = viewModelToInputConverter({ viewModel });
			const overrideInput = {
				...input,
				inSale: false,
			};

			mutationFunc({
				variables: {
					input: overrideInput,
				},
				refetchQueries: [
					{
						query: VoucherForCampaignListByHotelIdDocument,
						variables: { hotelID: input.hotelID },
					},
					{
						query: VoucherForCampaignNewItem_EditPage_Query,
						variables: { hotelID: input.hotelID },
					},
				],
			})
				.then(({ errors }) => {
					if (!errors) {
						notifyUserEventInfo({
							variant: "default",
							title: "キャンペーンを保存しました",
						});
						onResetViewModel();

						setTimeout(() => {
							onTransitionToCampaignTop(input.hotelID);
						}, 300);
					}
				})
				.finally(() => {
					hideLoadingScreenCallback();
				});
		},
		[
			hideLoadingScreenCallback,
			mutationFunc,
			onResetViewModel,
			onTransitionToCampaignTop,
			showLoadingScreenCallback,
		],
	);

	return { onSave };
};
